import { ref, computed } from 'vue';
import { supabase, supabaseAdmin } from '@/services/supabase';
import type { User, LoginCredentials, RegistrationData } from '@/types/user';
import type { Profile, ProfileInsert } from '@/types/supabase';
import { logger } from '@/services/logger';
import { BusinessRole } from '@/types/businessRole';
import { SystemRole } from '@/types/systemRole';

export function useAuth() {
  const user = ref<User | null>(null);
  const profile = ref<Profile | null>(null);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  const isAuthenticated = computed(() => !!user.value);

  /**
   * Initialize the auth state by checking for an existing session
   */
  async function init() {
    loading.value = true;
    try {
      logger.info('Initializing auth state');
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        logger.info('Session found', { userId: session.user.id });
        
        // Check if the session is still valid (if expires_at is available)
        const expiresAt = session.expires_at;
        if (expiresAt && new Date(expiresAt * 1000) < new Date()) {
          logger.warn('Session expired, refreshing token');
          
          // Refresh the session
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            logger.error('Error refreshing session:', { error: refreshError });
            throw refreshError;
          }
          
          if (refreshData.session) {
            logger.info('Session refreshed successfully');
            user.value = refreshData.session.user as User;
          } else {
            logger.warn('No session after refresh, user may need to login again');
            return;
          }
        } else {
          user.value = session.user as User;
        }
        
        // Fetch the user's profile
        try {
          await fetchProfile();
          logger.info('Profile fetched successfully');
        } catch (profileErr) {
          logger.error('Error fetching profile during init:', { error: profileErr });
          // Continue even if profile fetch fails
        }
      } else {
        logger.info('No session found, user is not authenticated');
      }
    } catch (err) {
      logger.error('Error initializing auth:', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch the user's profile from the profiles table
   */
  async function fetchProfile() {
    if (!user.value) {
      logger.warn('Attempted to fetch profile but user is not set');
      return;
    }
    
    logger.info('Fetching profile for user', { userId: user.value.id });
    
    try {
      // Try to fetch the profile
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.value.id)
        .single();
      
      // If profile exists, use it
      if (!err && data) {
        profile.value = data;
        
        // Attach profile to user
        if (user.value) {
          user.value.profile = data;
        }
        return;
      }
      
      // If profile doesn't exist (or there was an error), create one
      if (err && (err.code === 'PGRST116' || err.message.includes('multiple (or no) rows returned'))) {
        logger.info('Profile not found, creating a new one for user:', { userId: user.value.id });
        
        // Create a default profile for the user
        const profileData: ProfileInsert = {
          id: user.value.id,
          name: user.value.user_metadata?.full_name || '',
          username: user.value.email?.split('@')[0] || '',
          email: user.value.email || '',
          role: 'User', // Default role
          system_role: SystemRole.USER, // Default
          business_role: BusinessRole.PLANNER, // Default to planner (least privileged role) for business_role
          created_at: new Date().toISOString()
        };
        
        // Use the admin client to bypass RLS policies
        if (!supabaseAdmin) {
          logger.warn('Admin client not available, cannot create profile. Make sure VUE_APP_SUPABASE_SERVICE_KEY is set in .env');
          // Continue without a profile
          return;
        }
        
        const { data: newProfile, error: profileError } = await supabaseAdmin
          .from('profiles')
          .insert(profileData)
          .select()
          .single();
        
        if (profileError) {
          logger.error('Error creating profile:', { error: profileError });
          throw profileError;
        }
        
        logger.info('Profile created successfully', { profile: newProfile });
        
        profile.value = newProfile;
        
        // Attach profile to user
        if (user.value) {
          user.value.profile = newProfile;
        }
        
        return;
      }
      
      // If it's a different error, throw it
      if (err) throw err;
      
    } catch (err) {
      logger.error('Error fetching/creating profile', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Login with email and password
   */
  async function login({ email, password }: LoginCredentials) {
    loading.value = true;
    error.value = null;
    
    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (err) throw err;
      
      user.value = data.user as User;
      await fetchProfile();
      return data;
    } catch (err) {
      logger.error('Login error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Register a new user
   */
  async function register({ email, password, fullname, username }: RegistrationData) {
    loading.value = true;
    error.value = null;
    
    try {
      // Create the user in Supabase Auth
      const { data, error: err } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (err) throw err;
      
      user.value = data.user as User;
      
      // Create the user's profile
      if (data.user) {
        const profileData: ProfileInsert = {
          id: data.user.id,
          name: fullname,
          username,
          email,
          role: 'User', // Default role
          system_role: SystemRole.USER, // Default
          business_role: BusinessRole.PLANNER, // Default to planner (least privileged role) for business_role
          created_at: new Date().toISOString()
        };
        
        const { error: profileError } = await supabase
          .from('profiles')
          .insert(profileData);
        
        if (profileError) throw profileError;
        
        await fetchProfile();
      }
      
      return data;
    } catch (err) {
      logger.error('Registration error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logout the current user
   */
  async function logout() {
    loading.value = true;
    
    try {
      // Use the more forceful signOut option with scope: 'local'
      const { error: err } = await supabase.auth.signOut({
        scope: 'local' // This ensures all local session data is removed
      });
      
      if (err) throw err;
      
      // Clear user and profile state
      user.value = null;
      profile.value = null;
      
      // Clear any localStorage items related to Supabase auth
      localStorage.removeItem('supabase.auth.token');
      
      // Clear all cookies related to authentication
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
      
      // Force reload the page to ensure clean state
      window.location.reload();
    } catch (err) {
      logger.error('Logout error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update user profile
   */
  async function updateProfile(updates: Partial<Profile>) {
    
    // First, ensure we have a valid session
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (sessionData.session) {
        // Set the user from the session
        user.value = sessionData.session.user as User;
        
        // Try to fetch the profile if needed
        if (!profile.value) {
          await fetchProfile();
        }
      } else {
        throw new Error('User is not authenticated');
      }
    } catch (authErr) {
      logger.error('Error checking authentication before profile update', { error: authErr });
      throw new Error('Authentication error: ' + (authErr instanceof Error ? authErr.message : String(authErr)));
    }
    
    // Double-check that user is set after authentication check
    if (!user.value) {
      logger.error('User is still not set after authentication check');
      throw new Error('Cannot update profile: User not authenticated');
    }
    
    loading.value = true;
    error.value = null;
    
    try {
      // Log the update operation, especially for business_role changes
      if (updates.business_role) {
        logger.info('Updating user business role', {
          userId: user.value.id,
          oldRole: profile.value?.business_role,
          newRole: updates.business_role
        });
      }
      
      // Validate business_role if it's being updated
      if (updates.business_role && !Object.values(BusinessRole).includes(updates.business_role as BusinessRole)) {
        logger.warn('Invalid business role provided', { role: updates.business_role });
        throw new Error(`Invalid business role: ${updates.business_role}`);
      }
      
      // Perform the update operation
      const updateResult = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.value.id)
        .select()
        .single();
      
      const { data, error: err } = updateResult;
      
      if (err) {
        logger.error('Supabase error updating profile', { error: err });
        throw err;
      }
      
      if (!data) {
        logger.error('No data returned from profile update');
        throw new Error('Failed to update profile: No data returned');
      }
      
      logger.info('Profile updated successfully', {
        userId: user.value.id,
        updatedFields: Object.keys(updates)
      });
      
      // Update local state
      profile.value = data;
      
      // Update the user's profile
      if (user.value) {
        user.value.profile = data;
      }
      
      return data;
    } catch (err) {
      logger.error('Profile update error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update user password
   */
  async function updatePassword(currentPassword: string, newPassword: string) {
    if (!user.value) return;
    
    loading.value = true;
    error.value = null;
    
    try {
      // First verify the current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.value.email || '',
        password: currentPassword,
      });
      
      if (signInError) throw new Error('Current password is incorrect');
      
      // Then update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (updateError) throw updateError;
      
      return true;
    } catch (err) {
      logger.error('Password update error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  // Set up auth state change listener
  supabase.auth.onAuthStateChange(async (event, session) => {
    logger.info('Auth state changed', { event, userId: session?.user?.id });
    
    if (event === 'SIGNED_IN' && session) {
      user.value = session.user as User;
      try {
        await fetchProfile();
        logger.info('Profile fetched after sign in');
      } catch (err) {
        logger.error('Error fetching profile after sign in:', { error: err });
      }
    } else if (event === 'SIGNED_OUT') {
      logger.info('User signed out');
      user.value = null;
      profile.value = null;
    } else if (event === 'TOKEN_REFRESHED') {
      logger.info('Token refreshed');
      if (session) {
        user.value = session.user as User;
      }
    }
  });
/**
 * Check if the user is authenticated and refresh the token if needed
 * This can be called before making authenticated requests
 */
async function checkAndRefreshAuth() {
  logger.info('Checking authentication status');
  
  try {
    // Get the current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      logger.warn('No session found during auth check');
      return false;
    }
    
    // Update the user value
    user.value = session.user as User;
    
    // Check if the token needs to be refreshed
    const expiresAt = session.expires_at;
    if (expiresAt && new Date(expiresAt * 1000).getTime() - Date.now() < 60 * 60 * 1000) {
      logger.info('Token expiring soon, refreshing');
      
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        logger.error('Error refreshing token:', { error: refreshError });
        return false;
      }
      
      if (data.session) {
        logger.info('Token refreshed successfully');
        user.value = data.session.user as User;
      }
    }
    
    // If we don't have a profile yet, try to fetch it
    if (!profile.value && user.value) {
      try {
        await fetchProfile();
      } catch (err) {
        logger.error('Error fetching profile during auth check:', { error: err });
        // Continue even if profile fetch fails
      }
    }
    
    return true;
  } catch (err) {
    logger.error('Error checking auth status:', { error: err });
    return false;
  }
}

return {
  user,
  profile,
  loading,
  error,
  isAuthenticated,
  init,
  login,
  register,
  logout,
  updateProfile,
  updatePassword,
  checkAndRefreshAuth,
};
}
